import { ref } from 'vue';
import { useVueFlow } from '@vue-flow/core';
import type { Edge, XYPosition } from '@vue-flow/core';
// Use local NodeWithDimensions type throughout

// Locally extend Node type to include dimensions property
interface NodeWithDimensions {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: any;
  style?: any;
  dimensions?: { width: number; height: number };
  [key: string]: any;
}
import { Alg } from 'cubing/alg';
import { useDragAndDrop } from './useDragAndDrop'; // Import useDragAndDrop

export function useNodeManagement({ addNodes, addEdges, updateNodeData, setNodes, findNode }: any) {
  // Update a node's position using setNodes (VueFlow docs pattern)
  function updateNodePosition(id: string, position: { x: number; y: number }) {
    setNodes((nodes: NodeWithDimensions[]) => nodes.map(node =>
      node.id === id ? { ...node, position: { ...position } } : node
    ));
  }
  const { screenToFlowCoordinate } = useVueFlow();
  const nodeIdCounter = ref(1);

  const getNextNodeId = (): string => {
    const id = nodeIdCounter.value;
    nodeIdCounter.value++;
    return `n-${id}`; 
  };

  const resetNodes = (): NodeWithDimensions | null => {
    nodeIdCounter.value = 1; 
    const rootId = getNextNodeId(); 

    setNodes([]);
    const initialRootNode: NodeWithDimensions = { 
      id: rootId,
      type: 'twisty',
      position: { x: 0, y: 0 },
      data: { label: 'Solved', alg: '', targetHandleId: 'handle-b', rawAlgorithm: '', collapsed: false },
      style: { borderColor: '#ffffff', borderWidth: '8px', borderStyle: 'solid', borderRadius: '4px' },
    };
    addNodes([initialRootNode]);
    return initialRootNode; 
  };

  
  const handleSetTargetHandle = ({ nodeId, newTargetHandleId }: { nodeId: string, newTargetHandleId: string }) => {
    updateNodeData(nodeId, { targetHandleId: newTargetHandleId });
  };

  // Helper: measure half expanded dimensions, prefer real dimensions when available
  function getHalfDimensions(node: NodeWithDimensions): { halfHeight: number; halfWidth: number } {
    // Deduct handle width (26px) if dimensions are available, else fallback to empirical constants
    const rawHeight = node.dimensions?.height;
    const rawWidth = node.dimensions?.width;
    const adjustedHeight = rawHeight ? Math.max(rawHeight - 26, 0) : 376; // 188*2 fallback
    const adjustedWidth = rawWidth ? Math.max(rawWidth - 26, 0) : 324; // 162*2 fallback
    return {
      halfHeight: adjustedHeight / 2,
      halfWidth: adjustedWidth / 2,
    };
  }

  // Collapse logic: measure, collapse, translate
  function collapseNode(node: NodeWithDimensions) {
    const { halfHeight, halfWidth } = getHalfDimensions(node);
    updateNodeData(node.id, { collapsed: true });
    updateNodePosition(node.id, {
      x: node.position.x + halfWidth,
      y: node.position.y + halfHeight,
    });
  }

  // Expand logic: expand, measure, translate
  function expandNode(node: NodeWithDimensions) {
    updateNodeData(node.id, { collapsed: false });
    // After expanding, node.dimensions should update, but may require a next tick or delay
    // For now, measure immediately (may need improvement if dimensions update async)
    const { halfHeight, halfWidth } = getHalfDimensions(node);
    updateNodePosition(node.id, {
      x: node.position.x - halfWidth,
      y: node.position.y - halfHeight,
    });
  }

  // Orchestrator
  const toggleNodeCollapse = (nodeId: string) => {
    const node = findNode(nodeId) as NodeWithDimensions | undefined;
    if (!node) return;
    const isCollapsed = !!node.data.collapsed;
    if (!isCollapsed) {
      // Collapsing: measure, collapse, translate
      collapseNode(node);
    } else {
      // Expanding: expand, measure, translate
      expandNode(node);
    }
  };

  const { onDrop } = useDragAndDrop(screenToFlowCoordinate, addNodes, getNextNodeId); // Use onDrop from useDragAndDrop

  const generateAlgTree = (baseAlgString: string, mirrorAlgString: string) => {
    const rootNode = resetNodes();
    if (!rootNode) return;

    const createBranch = (parentNode: NodeWithDimensions, edgeAlg: string, offset: XYPosition) => {
      let currentParent = parentNode;
      for (let i = 0; i < 3; i++) {
        const newNodeId = getNextNodeId();
        const newNode: NodeWithDimensions = {
          id: newNodeId,
          type: 'twisty',
          position: { x: currentParent.position.x + offset.x, y: currentParent.position.y + offset.y },
          data: { label: edgeAlg, alg: edgeAlg, rawAlgorithm: edgeAlg, targetHandleId: 'handle-b', collapsed: false },
          style: { borderColor: '#ffffff', borderWidth: '8px', borderStyle: 'solid', borderRadius: '4px' },
        };
        addNodes([newNode]);

        // Add an edge connecting the parent node to the new node
        const newEdge: Edge = {
          id: `e${currentParent.id}-${newNodeId}`,
          source: currentParent.id,
          target: newNodeId,
          type: 'special',
          label: edgeAlg,
          data: { algorithm: edgeAlg },
          animated: true,
        };
        addEdges([newEdge]); // Ensure addEdges is called correctly
        currentParent = newNode;
      }
    };

    createBranch(rootNode, baseAlgString, { x: 300, y: 0 });
    createBranch(rootNode, new Alg(baseAlgString).invert().toString(), { x: -300, y: 0 });
    createBranch(rootNode, mirrorAlgString, { x: 0, y: -300 });
  };

  return { nodeIdCounter, getNextNodeId, resetNodes, generateAlgTree, handleSetTargetHandle, onDrop, toggleNodeCollapse, updateNodePosition };
}
